import { useEffect, useState } from 'react';
import { adminApi } from '@formosa/shared/api/admin';
import Pagination from '../../components/Pagination';
import {
  COPY,
  EMPTY_TRADE_FILTERS,
  PAGE_SIZE,
  TRADE_STATUS_OPTIONS,
  buildTradePageParams,
  formatAmount,
  formatDateCell,
  formatTradeStatus,
  maskIdentifier,
  tradeStatusClassName,
} from './travelManageConstants';

const TABLE_COLUMN_COUNT = 11;

function DetailRow({ label, value, className = '' }) {
  return (
    <div className="product-detail-row">
      <span className="product-detail-row__label">{label}</span>
      <span className={`product-detail-row__value ${className}`.trim()}>{value ?? '—'}</span>
    </div>
  );
}

export default function TradeListPage() {
  const [filters, setFilters] = useState(EMPTY_TRADE_FILTERS);
  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [detail, setDetail] = useState(null);

  async function load(pageNo = 1, nextFilters = filters) {
    setLoading(true);
    setError('');
    try {
      const data = await adminApi.travelTradePageQuery(
        buildTradePageParams(pageNo, nextFilters, PAGE_SIZE),
      );
      setRecords(data?.records || []);
      setPage(data?.current || pageNo);
      setTotalPages(data?.pages || 1);
    } catch (err) {
      setError(err.message || '載入失敗');
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function updateFilter(key, value) {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }

  function handleSearch(event) {
    event.preventDefault();
    load(1, filters);
  }

  function handleReset() {
    const reset = { ...EMPTY_TRADE_FILTERS };
    setFilters(reset);
    setError('');
    load(1, reset);
  }

  async function openDetail(tradeId) {
    setError('');
    try {
      const data = await adminApi.travelTradeDetail(tradeId);
      setDetail(data);
    } catch (err) {
      setError(err.message || '載入詳情失敗');
    }
  }

  return (
    <div className="page-card">
      <div className="section-header">
        <h2>
          交易管理
          <span className="muted">{COPY.readOnlyHint}</span>
        </h2>
      </div>

      <form className="filter-bar filter-bar-wrap" onSubmit={handleSearch}>
        <div className="form-field">
          <label htmlFor="ttTradeId">交易 ID</label>
          <input
            id="ttTradeId"
            value={filters.tradeId}
            onChange={(e) => updateFilter('tradeId', e.target.value)}
          />
        </div>
        <div className="form-field">
          <label htmlFor="ttGroupTripId">揪團 ID</label>
          <input
            id="ttGroupTripId"
            value={filters.groupTripId}
            onChange={(e) => updateFilter('groupTripId', e.target.value)}
          />
        </div>
        <div className="form-field">
          <label htmlFor="ttBuyerMemberId">買家用戶 ID</label>
          <input
            id="ttBuyerMemberId"
            value={filters.buyerMemberId}
            onChange={(e) => updateFilter('buyerMemberId', e.target.value)}
          />
        </div>
        <div className="form-field">
          <label htmlFor="ttHostMemberId">主辦用戶 ID</label>
          <input
            id="ttHostMemberId"
            value={filters.hostMemberId}
            onChange={(e) => updateFilter('hostMemberId', e.target.value)}
          />
        </div>
        <div className="form-field">
          <label htmlFor="ttStatus">{COPY.status}</label>
          <select
            id="ttStatus"
            value={filters.status}
            onChange={(e) => updateFilter('status', e.target.value)}
          >
            {TRADE_STATUS_OPTIONS.map((opt) => (
              <option key={opt.value || 'all'} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        <div className="form-field">
          <label htmlFor="ttCurrencyCode">幣別</label>
          <input
            id="ttCurrencyCode"
            value={filters.currencyCode}
            onChange={(e) => updateFilter('currencyCode', e.target.value)}
          />
        </div>
        <div className="filter-actions">
          <button type="submit" className="btn btn-primary">{COPY.search}</button>
          <button type="button" className="btn btn-secondary" onClick={handleReset}>
            {COPY.reset}
          </button>
        </div>
      </form>

      {loading ? <p>載入中…</p> : null}
      {error ? <div className="alert alert-error">{error}</div> : null}

      <div className="table-scroll">
        <table className="data-table">
          <thead>
            <tr>
              <th>交易 ID</th>
              <th>揪團 ID</th>
              <th>買家</th>
              <th>主辦</th>
              <th>{COPY.status}</th>
              <th>幣別</th>
              <th>金額</th>
              <th>平台費率</th>
              <th>平台費</th>
              <th>{COPY.createdAt}</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {!loading && records.length === 0 ? (
              <tr>
                <td colSpan={TABLE_COLUMN_COUNT} className="empty-cell">
                  {COPY.emptyList}
                </td>
              </tr>
            ) : (
              records.map((row) => (
                <tr key={row.tradeId}>
                  <td>{row.tradeId}</td>
                  <td>{row.groupTripId}</td>
                  <td>{row.buyerMemberId}</td>
                  <td>{row.hostMemberId}</td>
                  <td className={tradeStatusClassName(row.status)}>
                    {formatTradeStatus(row.status)}
                  </td>
                  <td>{row.currencyCode}</td>
                  <td>{formatAmount(row.grossAmount)}</td>
                  <td>{row.platformFeeRate ?? '—'}</td>
                  <td>{formatAmount(row.platformFeeAmount)}</td>
                  <td>{formatDateCell(row.createTime)}</td>
                  <td>
                    <button type="button" className="btn btn-link" onClick={() => openDetail(row.tradeId)}>
                      {COPY.view}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Pagination page={page} totalPages={totalPages} onChange={(p) => load(p, filters)} />

      {detail ? (
        <div className="admin-modal-overlay" role="presentation" onClick={() => setDetail(null)}>
          <div
            className="admin-modal admin-modal--wide"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="admin-modal__header">
              <h3>
                交易詳情 #{detail.tradeId}
                <span className="muted"> {COPY.readOnlyHint}</span>
              </h3>
              <button type="button" className="admin-modal__close" onClick={() => setDetail(null)}>
                ×
              </button>
            </div>
            <div className="admin-modal__body">
              <div className="product-detail-grid">
                <DetailRow
                  label={COPY.status}
                  value={formatTradeStatus(detail.status)}
                  className={tradeStatusClassName(detail.status)}
                />
                <DetailRow label="揪團 ID" value={detail.groupTripId} />
                <DetailRow label="買家 / 主辦" value={`${detail.buyerMemberId} / ${detail.hostMemberId}`} />
                <DetailRow
                  label="金額"
                  value={`${formatAmount(detail.grossAmount)} ${detail.currencyCode ?? ''}`.trim()}
                />
                <DetailRow
                  label="平台費"
                  value={`${formatAmount(detail.platformFeeAmount)}（費率 ${detail.platformFeeRate ?? '—'}）`}
                />
                <DetailRow label="主辦應收" value={formatAmount(detail.hostReceivableAmount)} />
                <DetailRow
                  label="收款請求"
                  value={detail.hasCollectionRequest ? '是' : '否'}
                />
                <DetailRow
                  label="Marketplace 資金 ID"
                  value={maskIdentifier(detail.marketplaceFundsId)}
                  title={detail.marketplaceFundsId ? String(detail.marketplaceFundsId) : undefined}
                />
              </div>
            </div>
            <div className="admin-modal__footer">
              <button type="button" className="btn btn-secondary" onClick={() => setDetail(null)}>
                關閉
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
